import type { Application } from "@hotwired/stimulus";
import { AccordionController } from "./controllers/accordion_controller";
import { AlertDialogController } from "./controllers/alert_dialog_controller";
import { AnnouncerController } from "./controllers/announcer_controller";
import { AspectRatioController } from "./controllers/aspect_ratio_controller";
import { AutoSubmitController } from "./controllers/auto_submit_controller";
import { AvatarController } from "./controllers/avatar_controller";
import { BreadcrumbController } from "./controllers/breadcrumb_controller";
import { BulkSelectController } from "./controllers/bulk_select_controller";
import { CalendarController } from "./controllers/calendar_controller";
import { CarouselController } from "./controllers/carousel_controller";
import { CharacterCounterController } from "./controllers/character_counter_controller";
import { CheckboxController } from "./controllers/checkbox_controller";
import { ClipboardController } from "./controllers/clipboard_controller";
import { CollapsibleController } from "./controllers/collapsible_controller";
import { ColorPickerController } from "./controllers/color_picker_controller";
import { ComboboxController } from "./controllers/combobox_controller";
import { CommandPaletteController } from "./controllers/command_palette_controller";
import { ConditionalFieldsController } from "./controllers/conditional_fields_controller";
import { ConfirmController } from "./controllers/confirm_controller";
import { ContextMenuController } from "./controllers/context_menu_controller";
import { CountUpController } from "./controllers/count_up_controller";
import { CountdownController } from "./controllers/countdown_controller";
import { CurrencyInputController } from "./controllers/currency_input_controller";
import { DataGridController } from "./controllers/data_grid_controller";
import { DateRangePickerController } from "./controllers/date_range_picker_controller";
import { DialogController } from "./controllers/dialog_controller";
import { DirectUploadController } from "./controllers/direct_upload_controller";
import { DirtyFormController } from "./controllers/dirty_form_controller";
import { DismissibleController } from "./controllers/dismissible_controller";
import { DrawerController } from "./controllers/drawer_controller";
import { DropdownController } from "./controllers/dropdown_controller";
import { EditableController } from "./controllers/editable_controller";
import { EmptyStateController } from "./controllers/empty_state_controller";
import { FileDropzoneController } from "./controllers/file_dropzone_controller";
import { FilterController } from "./controllers/filter_controller";
import { FlashController } from "./controllers/flash_controller";
import { FocusController } from "./controllers/focus_controller";
import { FormFieldController } from "./controllers/form_field_controller";
import { FormValidationController } from "./controllers/form_validation_controller";
import { FrameLoadingController } from "./controllers/frame_loading_controller";
import { HighlightController } from "./controllers/highlight_controller";
import { HoverCardController } from "./controllers/hover_card_controller";
import { IdleController } from "./controllers/idle_controller";
import { InputMaskController } from "./controllers/input_mask_controller";
import { IntersectionController } from "./controllers/intersection_controller";
import { LazyFrameController } from "./controllers/lazy_frame_controller";
import { ListboxController } from "./controllers/listbox_controller";
import { LocalTimeController } from "./controllers/local_time_controller";
import { MasonryController } from "./controllers/masonry_controller";
import { MenuController } from "./controllers/menu_controller";
import { MenubarController } from "./controllers/menubar_controller";
import { MeterController } from "./controllers/meter_controller";
import { MultiSelectController } from "./controllers/multi_select_controller";
import { NavigationMenuController } from "./controllers/navigation_menu_controller";
import { NestedFormController } from "./controllers/nested_form_controller";
import { NetworkStatusController } from "./controllers/network_status_controller";
import { NumberInputController } from "./controllers/number_input_controller";
import { OptimisticController } from "./controllers/optimistic_controller";
import { OtpController } from "./controllers/otp_controller";
import { OverflowIndicatorController } from "./controllers/overflow_indicator_controller";
import { OverflowMenuController } from "./controllers/overflow_menu_controller";
import { PaginationController } from "./controllers/pagination_controller";
import { PasswordRevealController } from "./controllers/password_reveal_controller";
import { PasswordStrengthController } from "./controllers/password_strength_controller";
import { PersistController } from "./controllers/persist_controller";
import { PointerDragController } from "./controllers/pointer_drag_controller";
import { PopoverController } from "./controllers/popover_controller";
import { PortalController } from "./controllers/portal_controller";
import { PreviewGuardController } from "./controllers/preview_guard_controller";
import { ProgressController } from "./controllers/progress_controller";
import { RadioGroupController } from "./controllers/radio_group_controller";
import { RangeSliderController } from "./controllers/range_slider_controller";
import { RatingController } from "./controllers/rating_controller";
import { ReadMoreController } from "./controllers/read_more_controller";
import { ReadingProgressController } from "./controllers/reading_progress_controller";
import { RelativeTimeController } from "./controllers/relative_time_controller";
import { ResetOnRestoreController } from "./controllers/reset_on_restore_controller";
import { ResizableController } from "./controllers/resizable_controller";
import { RovingController } from "./controllers/roving_controller";
import { ScrollAreaController } from "./controllers/scroll_area_controller";
import { ScrollRestoreController } from "./controllers/scroll_restore_controller";
import { ScrollVisibilityController } from "./controllers/scroll_visibility_controller";
import { ScrollspyController } from "./controllers/scrollspy_controller";
import { SeparatorController } from "./controllers/separator_controller";
import { SidebarController } from "./controllers/sidebar_controller";
import { SkeletonController } from "./controllers/skeleton_controller";
import { SliderController } from "./controllers/slider_controller";
import { SmartStickyHeaderController } from "./controllers/smart_sticky_header_controller";
import { SortableController } from "./controllers/sortable_controller";
import { SpinnerController } from "./controllers/spinner_controller";
import { StepIndicatorController } from "./controllers/step_indicator_controller";
import { StepperController } from "./controllers/stepper_controller";
import { StickToBottomController } from "./controllers/stick_to_bottom_controller";
import { StickyObserverController } from "./controllers/sticky_observer_controller";
import { SubmitOnceController } from "./controllers/submit_once_controller";
import { SwitchController } from "./controllers/switch_controller";
import { TabsController } from "./controllers/tabs_controller";
import { TagsInputController } from "./controllers/tags_input_controller";
import { TextareaAutosizeController } from "./controllers/textarea_autosize_controller";
import { ThemeController } from "./controllers/theme_controller";
import { TimePickerController } from "./controllers/time_picker_controller";
import { ToastController } from "./controllers/toast_controller";
import { ToggleGroupController } from "./controllers/toggle_group_controller";
import { ToolbarController } from "./controllers/toolbar_controller";
import { TooltipController } from "./controllers/tooltip_controller";
import { TransitionController } from "./controllers/transition_controller";
import { TreeViewController } from "./controllers/tree_view_controller";
import type { StateReason } from "./utils/state_reason";

export { prepareInputMaskForm } from "./controllers/input_mask_controller";
export type { StateReason };

export {
  AccordionController,
  AlertDialogController,
  AnnouncerController,
  AspectRatioController,
  AutoSubmitController,
  AvatarController,
  BreadcrumbController,
  BulkSelectController,
  CalendarController,
  CarouselController,
  CharacterCounterController,
  CheckboxController,
  ClipboardController,
  CollapsibleController,
  ColorPickerController,
  ComboboxController,
  CommandPaletteController,
  ConditionalFieldsController,
  ConfirmController,
  ContextMenuController,
  CountdownController,
  CountUpController,
  CurrencyInputController,
  DataGridController,
  DateRangePickerController,
  DialogController,
  DirectUploadController,
  DirtyFormController,
  DismissibleController,
  DrawerController,
  DropdownController,
  EditableController,
  EmptyStateController,
  FileDropzoneController,
  FilterController,
  FlashController,
  FocusController,
  FormFieldController,
  FormValidationController,
  FrameLoadingController,
  HighlightController,
  HoverCardController,
  IdleController,
  InputMaskController,
  IntersectionController,
  LazyFrameController,
  ListboxController,
  LocalTimeController,
  MasonryController,
  MenubarController,
  MenuController,
  MeterController,
  MultiSelectController,
  NavigationMenuController,
  NestedFormController,
  NetworkStatusController,
  NumberInputController,
  OptimisticController,
  OtpController,
  OverflowIndicatorController,
  OverflowMenuController,
  PaginationController,
  PasswordRevealController,
  PasswordStrengthController,
  PersistController,
  PointerDragController,
  PopoverController,
  PortalController,
  PreviewGuardController,
  ProgressController,
  RadioGroupController,
  RangeSliderController,
  RatingController,
  ReadingProgressController,
  ReadMoreController,
  RelativeTimeController,
  ResetOnRestoreController,
  ResizableController,
  RovingController,
  ScrollAreaController,
  ScrollRestoreController,
  ScrollspyController,
  ScrollVisibilityController,
  SeparatorController,
  SidebarController,
  SkeletonController,
  SliderController,
  SmartStickyHeaderController,
  SortableController,
  SpinnerController,
  StepIndicatorController,
  StepperController,
  StickToBottomController,
  StickyObserverController,
  SubmitOnceController,
  SwitchController,
  TabsController,
  TagsInputController,
  TextareaAutosizeController,
  ThemeController,
  TimePickerController,
  ToastController,
  ToggleGroupController,
  ToolbarController,
  TooltipController,
  TransitionController,
  TreeViewController,
};

/**
 * Maps the core controller identifiers to their controller classes. The opt-in
 * `stimeo-ui/positioning` and `stimeo-ui/cable` entry points export their own maps.
 * Identifiers use the `stimeo--` namespace.
 */
export const stimeoControllers = {
  "stimeo--accordion": AccordionController,
  "stimeo--alert-dialog": AlertDialogController,
  "stimeo--announcer": AnnouncerController,
  "stimeo--aspect-ratio": AspectRatioController,
  "stimeo--auto-submit": AutoSubmitController,
  "stimeo--avatar": AvatarController,
  "stimeo--breadcrumb": BreadcrumbController,
  "stimeo--bulk-select": BulkSelectController,
  "stimeo--calendar": CalendarController,
  "stimeo--carousel": CarouselController,
  "stimeo--character-counter": CharacterCounterController,
  "stimeo--checkbox": CheckboxController,
  "stimeo--clipboard": ClipboardController,
  "stimeo--collapsible": CollapsibleController,
  "stimeo--color-picker": ColorPickerController,
  "stimeo--combobox": ComboboxController,
  "stimeo--command-palette": CommandPaletteController,
  "stimeo--conditional-fields": ConditionalFieldsController,
  "stimeo--confirm": ConfirmController,
  "stimeo--context-menu": ContextMenuController,
  "stimeo--count-up": CountUpController,
  "stimeo--countdown": CountdownController,
  "stimeo--currency-input": CurrencyInputController,
  "stimeo--data-grid": DataGridController,
  "stimeo--date-range-picker": DateRangePickerController,
  "stimeo--dialog": DialogController,
  "stimeo--direct-upload": DirectUploadController,
  "stimeo--dirty-form": DirtyFormController,
  "stimeo--dismissible": DismissibleController,
  "stimeo--drawer": DrawerController,
  "stimeo--dropdown": DropdownController,
  "stimeo--editable": EditableController,
  "stimeo--empty-state": EmptyStateController,
  "stimeo--file-dropzone": FileDropzoneController,
  "stimeo--filter": FilterController,
  "stimeo--flash": FlashController,
  "stimeo--focus": FocusController,
  "stimeo--form-field": FormFieldController,
  "stimeo--form-validation": FormValidationController,
  "stimeo--frame-loading": FrameLoadingController,
  "stimeo--highlight": HighlightController,
  "stimeo--hover-card": HoverCardController,
  "stimeo--idle": IdleController,
  "stimeo--input-mask": InputMaskController,
  "stimeo--intersection": IntersectionController,
  "stimeo--lazy-frame": LazyFrameController,
  "stimeo--listbox": ListboxController,
  "stimeo--local-time": LocalTimeController,
  "stimeo--masonry": MasonryController,
  "stimeo--menu": MenuController,
  "stimeo--menubar": MenubarController,
  "stimeo--meter": MeterController,
  "stimeo--multi-select": MultiSelectController,
  "stimeo--navigation-menu": NavigationMenuController,
  "stimeo--nested-form": NestedFormController,
  "stimeo--network-status": NetworkStatusController,
  "stimeo--number-input": NumberInputController,
  "stimeo--optimistic": OptimisticController,
  "stimeo--otp": OtpController,
  "stimeo--overflow-indicator": OverflowIndicatorController,
  "stimeo--overflow-menu": OverflowMenuController,
  "stimeo--pagination": PaginationController,
  "stimeo--password-reveal": PasswordRevealController,
  "stimeo--password-strength": PasswordStrengthController,
  "stimeo--persist": PersistController,
  "stimeo--pointer-drag": PointerDragController,
  "stimeo--popover": PopoverController,
  "stimeo--portal": PortalController,
  "stimeo--preview-guard": PreviewGuardController,
  "stimeo--progress": ProgressController,
  "stimeo--radio-group": RadioGroupController,
  "stimeo--range-slider": RangeSliderController,
  "stimeo--rating": RatingController,
  "stimeo--read-more": ReadMoreController,
  "stimeo--reading-progress": ReadingProgressController,
  "stimeo--relative-time": RelativeTimeController,
  "stimeo--reset-on-restore": ResetOnRestoreController,
  "stimeo--resizable": ResizableController,
  "stimeo--roving": RovingController,
  "stimeo--scroll-area": ScrollAreaController,
  "stimeo--scroll-restore": ScrollRestoreController,
  "stimeo--scroll-visibility": ScrollVisibilityController,
  "stimeo--scrollspy": ScrollspyController,
  "stimeo--separator": SeparatorController,
  "stimeo--sidebar": SidebarController,
  "stimeo--skeleton": SkeletonController,
  "stimeo--slider": SliderController,
  "stimeo--smart-sticky-header": SmartStickyHeaderController,
  "stimeo--sortable": SortableController,
  "stimeo--spinner": SpinnerController,
  "stimeo--step-indicator": StepIndicatorController,
  "stimeo--stepper": StepperController,
  "stimeo--stick-to-bottom": StickToBottomController,
  "stimeo--sticky-observer": StickyObserverController,
  "stimeo--submit-once": SubmitOnceController,
  "stimeo--switch": SwitchController,
  "stimeo--tabs": TabsController,
  "stimeo--tags-input": TagsInputController,
  "stimeo--textarea-autosize": TextareaAutosizeController,
  "stimeo--theme": ThemeController,
  "stimeo--time-picker": TimePickerController,
  "stimeo--toast": ToastController,
  "stimeo--toggle-group": ToggleGroupController,
  "stimeo--toolbar": ToolbarController,
  "stimeo--tooltip": TooltipController,
  "stimeo--transition": TransitionController,
  "stimeo--tree-view": TreeViewController,
} as const;

/**
 * Registers every controller in {@link stimeoControllers} on a Stimulus Application.
 * The opt-in `stimeo-ui/positioning` and `stimeo-ui/cable` entry points register
 * theirs separately.
 *
 * @param application - The Stimulus application to register controllers on.
 *
 * @example
 * ```ts
 * import { Application } from "@hotwired/stimulus";
 * import { registerStimeo } from "stimeo-ui";
 *
 * const application = Application.start();
 * registerStimeo(application);
 * ```
 */
export function registerStimeo(application: Application): void {
  for (const [identifier, controller] of Object.entries(stimeoControllers)) {
    application.register(identifier, controller);
  }
}
